import { createListenerMiddleware } from '@reduxjs/toolkit';
import { NetworkQuality } from 'agora-rtc-react';
import { logger, monitoring } from 'common/services';
import { throttle } from 'lodash-es';
import { RootState } from 'store';

import { agoraActions } from './agora.actions';
import {
  audioTrackCreationCompletedEffect,
  audioTrackCreationFailedEffect,
  audioTrackTogglingFailedEffect,
  cameraDeviceChangeCompletedEffect,
  cameraDeviceChangeFailedEffect,
  microphoneDeviceChangeCompletedEffect,
  microphoneDeviceChangeFailedEffect,
  videoTrackCreationCompletedEffect,
  videoTrackCreationFailedEffect,
  videoTrackTogglingFailedEffect,
} from './agora.effects';
import { selectAgoraSupportedCloudProxyOptions } from './agora.selectors';

export const agoraListener = createListenerMiddleware<RootState>();

agoraListener.startListening({
  actionCreator: agoraActions.joined,
  effect: () => {
    logger.info('[agoraListener] User joined channel');
    monitoring.trackAction('broadcast.user_joined_successfully');
  },
});

agoraListener.startListening({
  actionCreator: agoraActions.left,
  effect: () => {
    logger.info('[agoraListener] User left channel');
  },
});

const trackNetworkQuality = throttle(({ downlinkNetworkQuality, uplinkNetworkQuality }: NetworkQuality) => {
  monitoring.trackAction('broadcast.network_quality', {
    downlinkNetworkQuality,
    uplinkNetworkQuality,
  });
}, 30_000);

agoraListener.startListening({
  actionCreator: agoraActions.networkQuality.changed,
  effect: ({ payload }) => {
    trackNetworkQuality(payload);
  },
});

agoraListener.startListening({
  actionCreator: agoraActions.stream.fellBack,
  effect: ({ payload: uid }) => {
    logger.info('[agoraListener] Stream fell back', { uid });
    monitoring.trackAction('video_service.stream_fell_back', { uid });
  },
});

agoraListener.startListening({
  actionCreator: agoraActions.stream.recovered,
  effect: ({ payload: uid }) => {
    logger.info('[agoraListener] Stream recovered', { uid });
    monitoring.trackAction('video_service.stream_recovered', { uid });
  },
});

// Audio related

agoraListener.startListening({
  actionCreator: agoraActions.audioTrack.creation.failed,
  effect: audioTrackCreationFailedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.audioTrack.creation.completed,
  effect: audioTrackCreationCompletedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.audioTrack.toggling.failed,
  effect: audioTrackTogglingFailedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.audioTrack.deviceChange.completed,
  effect: microphoneDeviceChangeCompletedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.audioTrack.deviceChange.failed,
  effect: microphoneDeviceChangeFailedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.audioTrack.published,
  effect: () => {
    logger.info('[agoraListener] Audio track published');
  },
});

// Video related

agoraListener.startListening({
  actionCreator: agoraActions.videoTrack.creation.failed,
  effect: videoTrackCreationFailedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.videoTrack.creation.completed,
  effect: videoTrackCreationCompletedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.videoTrack.toggling.failed,
  effect: videoTrackTogglingFailedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.videoTrack.deviceChange.completed,
  effect: cameraDeviceChangeCompletedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.videoTrack.deviceChange.failed,
  effect: cameraDeviceChangeFailedEffect,
});

agoraListener.startListening({
  actionCreator: agoraActions.videoTrack.published,
  effect: () => {
    logger.info('[agoraListener] Video track published');
  },
});

agoraListener.startListening({
  actionCreator: agoraActions.connectionTest.failed,
  effect: ({ payload }) => {
    logger.warn('[agoraListener] Error while detecting cloud proxy', { payload });
    monitoring.trackAction('broadcast.detect_cloud_proxy_error');
  },
});

agoraListener.startListening({
  actionCreator: agoraActions.connectionTest.completed,
  effect: (_, { getState }) => {
    const supportedCloudProxyOptions = selectAgoraSupportedCloudProxyOptions(getState());
    if (supportedCloudProxyOptions.length === 0) {
      logger.warn('[agoraListener] no cloud proxy supported');
      monitoring.trackAction('broadcast.no_cloud_proxy_supported');
    }
  },
});
